import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchPendingPosts, approvePost, hidePost, moderatorDeletePost,
  fetchMe, isModerator, CurrentUser,
} from '../lib/community-api';

interface Props {
  isLoggedIn: boolean;
}

type PendingPost = {
  id: number;
  attributes?: any;
};

export default function CommunityModeration({ isLoggedIn }: Props) {
  const navigate = useNavigate();
  const [me, setMe] = useState<CurrentUser | null>(null);
  const [loadingMe, setLoadingMe] = useState(true);
  const [posts, setPosts] = useState<PendingPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
      return;
    }
    fetchMe().then((u) => { setMe(u); setLoadingMe(false); });
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (loadingMe) return;
    if (!isModerator(me)) return;
    loadQueue();
  }, [loadingMe, me]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadQueue = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPendingPosts(1, 50);
      setPosts(res?.data || []);
    } catch (e: any) {
      setError('Failed to load queue.');
    } finally {
      setLoading(false);
    }
  };

  // Strapi 5 returns a flat shape: { id, content, user: {...}, ... }
  // But keep a fallback in case populate wraps with `attributes`.
  const getField = (post: PendingPost, key: string) => {
    return (post as any)[key] ?? post.attributes?.[key];
  };

  const getRel = (post: PendingPost, key: string) => {
    const direct = (post as any)[key];
    if (direct && (direct.username || direct.name || direct.url || Array.isArray(direct))) return direct;
    const attr = post.attributes?.[key];
    if (attr?.data?.attributes) return attr.data.attributes;
    if (attr?.data) return attr.data;
    return direct ?? attr;
  };

  const handleApprove = async (id: number) => {
    setBusy(id);
    try {
      await approvePost(id);
      setPosts((list) => list.filter((p) => p.id !== id));
    } catch {
      alert('Failed to approve.');
    } finally {
      setBusy(null);
    }
  };

  const handleHide = async (id: number) => {
    if (!window.confirm('Hide this post?')) return;
    setBusy(id);
    try {
      await hidePost(id);
      setPosts((list) => list.filter((p) => p.id !== id));
    } catch {
      alert('Failed to hide.');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Permanently delete this post + all its comments?')) return;
    setBusy(id);
    try {
      await moderatorDeletePost(id);
      setPosts((list) => list.filter((p) => p.id !== id));
    } catch {
      alert('Failed to delete.');
    } finally {
      setBusy(null);
    }
  };

  if (loadingMe) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <span className="text-gray-400 text-sm">Loading…</span>
      </div>
    );
  }

  if (!isModerator(me)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center max-w-sm">
          <h1 className="text-lg font-bold text-gray-900">Moderators only</h1>
          <p className="text-sm text-gray-500 mt-2">This page is for community moderators.</p>
          <Link to="/community" className="inline-block mt-4 text-sm text-primary font-semibold">Back to Community</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Moderation Queue — EatMeat Community</title>
      </Helmet>

      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 md:py-5 flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">Moderation Queue</h1>
            <p className="text-gray-500 text-xs">Posts awaiting review</p>
          </div>
          <Link to="/community" className="text-xs text-gray-500 hover:text-gray-700">← Back to Community</Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center text-sm text-gray-400 py-10">Loading…</div>
        ) : error ? (
          <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <p className="text-3xl mb-2">✅</p>
            <p className="text-sm text-gray-500">No posts waiting for review.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => {
              const content = getField(post, 'content') || '';
              const createdAt = getField(post, 'createdAt');
              const user = getRel(post, 'user');
              const category = getRel(post, 'category');
              const images = getRel(post, 'images');
              const imageCount = Array.isArray(images) ? images.length : Array.isArray(images?.data) ? images.data.length : 0;
              return (
                <div key={post.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-gray-800">{user?.username || 'Anonymous'}</span>
                        {category?.name && <span className="text-gray-300">•</span>}
                        {category?.name && <span className="text-gray-500 text-xs">{category.icon} {category.name}</span>}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {createdAt ? new Date(createdAt).toLocaleString() : ''}
                        {imageCount > 0 ? ` · 📷 ${imageCount} image${imageCount > 1 ? 's' : ''}` : ''}
                      </p>
                      <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap break-words">{content}</p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 justify-end">
                    <button
                      disabled={busy === post.id}
                      onClick={() => handleHide(post.id)}
                      className="text-xs px-3 py-1.5 rounded-md bg-yellow-50 text-yellow-800 hover:bg-yellow-100 font-semibold disabled:opacity-50"
                    >Hide</button>
                    <button
                      disabled={busy === post.id}
                      onClick={() => handleDelete(post.id)}
                      className="text-xs px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold disabled:opacity-50"
                    >Delete</button>
                    <button
                      disabled={busy === post.id}
                      onClick={() => handleApprove(post.id)}
                      className="text-xs px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 font-semibold disabled:opacity-50"
                    >Approve</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
